import Text from '@deprecated-ui/common/Text';
import shallow from 'zustand/shallow';
import { useEffect } from 'react';
import Icon from '@ant-design/icons';
import { formatPercentage } from 'utils/number';
import SkeletonWrapper from 'global/Securities/components/SkeletonWrapper';
import { useBondPortfolioStore } from 'global/TradingModal/RealTradingModal/ModalBonds/stores/useBondPortfolioStore';
import Collapsible from 'global/Collapsible';
import InfoSvg from 'global/Securities/components/Icons/InfoSvg';
import { BondInfoType } from 'global/TradingModal/RealTradingModal/ModalBonds/constants';
import { Flex } from 'features/securities/styled';
import { ChevronRightSvg } from 'features/day-trade/components/Icons';
import { BorderedContainer } from 'global/TradingModal/RealTradingModal/ModalBonds/styled';
import { Divider } from 'antd';
import {
  formatBondCurrency,
  formatBondDate,
} from 'features/securities/withdrawal/utils';
import { useBondInfoModal } from 'global/TradingModal/RealTradingModal/ModalBonds/stores/useBondInfoModal';
import { InfoTable } from '../DetailModal/InfoTable';
import { PortfolioSkeletonBlock, SectionButton } from './styled';
import PnLCurrency from './PnLCurrency';
import ToHistoryButton from './ToHistoryButton';

interface Props {
  symbol: string;
  onCouponHistoryClick?: () => void;
}

function BondModalDetail({ symbol, onCouponHistoryClick }: Readonly<Props>) {
  const openInfoModal = useBondInfoModal((s) => s.openInfoModal);

  const [details, isLoading, loadPortfolioDetails, couponHistories, product] =
    useBondPortfolioStore(
      (s) => [
        s.detailBySymbol,
        s.isLoading,
        s.loadPortfolioDetails,
        s.couponHistories,
        s.product,
      ],
      shallow,
    );

  const handleCouponReceiptClick = () => {
    onCouponHistoryClick();
  };

  // merge histories
  let histories =
    couponHistories?.histories?.flatMap((history) => history.items) || [];

  if (histories.length > 2) {
    histories = histories.slice(0, 2);
  }

  useEffect(() => {
    loadPortfolioDetails(symbol, true);
  }, [symbol]);

  return (
    <>
      <SkeletonWrapper
        skeleton={<PortfolioSkeletonBlock height={64} />}
        loading={isLoading}
      >
        <BorderedContainer>
          {/* @ts-expect-error */}
          <Flex align="center" justify="space-between" pb="4px">
            <Flex align="center">
              <Text data-cy="bond-invested-label">Total Modal Investasi</Text>
            </Flex>
            <Text data-cy="bond-invested-content">
              {formatBondCurrency(details?.total_portfolio)}
            </Text>
          </Flex>
          {details?.seller_coupon > 0 && (
            // @ts-expect-error
            <Flex align="center" justify="space-between" pb="4px">
              <Flex align="center">
                <Text data-cy="bond-invested-label">Kupon Penjual&nbsp;</Text>
                <Icon
                  component={InfoSvg}
                  style={{ color: '#969696' }}
                  onClick={() =>
                    openInfoModal(BondInfoType.ACCRUED_INTEREST_SELLER)
                  }
                />
              </Flex>
              <Text data-cy="bond-invested-content">
                {formatBondCurrency(details?.seller_coupon)}
              </Text>
            </Flex>
          )}

          <Divider
            css={`
              margin: 2px;
            `}
          />
          <Collapsible
            header={
              <Flex align="center" justify="space-between">
                <Flex direction="column">
                  <Flex align="center">
                    {/* @ts-expect-error */}
                    <Text data-cy="bond-pl-label" mr="4px">
                      P&L (Unrealized)
                    </Text>
                  </Flex>
                </Flex>
                <PnLCurrency
                  dataCy="bond-pl-content"
                  amount={details?.profit_loss.amount}
                  percentage={details?.profit_loss.percentage}
                />
              </Flex>
            }
          >
            <div>
              {/* @ts-expect-error */}
              <Flex align="center" justify="space-between" py="8px">
                <Flex align="center">
                  {/* @ts-expect-error */}
                  <Text data-cy="bond-accrued-interest-label" mr="4px">
                    Kupon Berjalan
                  </Text>
                  <Icon
                    component={InfoSvg}
                    style={{ color: '#969696' }}
                    onClick={() => openInfoModal(BondInfoType.ACCRUED_INTEREST)}
                  />
                </Flex>
                <PnLCurrency
                  dataCy="bond-accrued-interest-content"
                  amount={details?.accrued_interest}
                />
              </Flex>
              {/* @ts-expect-error */}
              <Flex align="center" justify="space-between" py="8px">
                <Flex align="center">
                  {/* @ts-expect-error */}
                  <Text data-cy="bond-capital-gain-loss-label" mr="4px">
                    Capital Gain / Loss
                  </Text>
                  <Icon
                    component={InfoSvg}
                    style={{ color: '#969696' }}
                    onClick={() =>
                      openInfoModal(BondInfoType.CAPITAL_GAIN_LOSS)
                    }
                  />
                </Flex>
                <PnLCurrency
                  dataCy="bond-capital-gain-loss-content"
                  amount={details?.capital_gain_loss.amount}
                  percentage={details?.capital_gain_loss.percentage}
                />
              </Flex>
            </div>
          </Collapsible>
        </BorderedContainer>
      </SkeletonWrapper>

      <SkeletonWrapper
        skeleton={<PortfolioSkeletonBlock height={55} />}
        loading={isLoading}
      >
        <BorderedContainer>
          <Collapsible
            header={
              <Flex align="center" justify="space-between">
                <Flex direction="column" align="start">
                  <Flex align="center">
                    {/* @ts-expect-error */}
                    <Text data-cy="bond-total-pl-label" mr="4px">
                      Total Keuntungan
                    </Text>
                  </Flex>
                  <Text size="12px" color="#B5B5B5">
                    Selama Investasi
                  </Text>
                </Flex>

                <PnLCurrency
                  dataCy="bond-total-pl-content"
                  amount={details?.total_profit_loss?.amount}
                  percentage={details?.total_profit_loss?.percentage}
                />
              </Flex>
            }
          >
            <div>
              {/* @ts-expect-error */}
              <Flex align="center" justify="space-between" py="8px">
                <Flex align="center">
                  {/* @ts-expect-error */}
                  <Text data-cy="bond-daily-accrued-interest-label" mr="4px">
                    Total Kupon Harian
                  </Text>
                  <Icon
                    component={InfoSvg}
                    style={{ color: '#969696' }}
                    onClick={() =>
                      openInfoModal(BondInfoType.TOTAL_DAILY_COUPON)
                    }
                  />
                </Flex>
                <PnLCurrency
                  dataCy="bond-daily-accrued-interest-content"
                  amount={details?.daily_accrued_interest.amount}
                  percentage={details?.daily_accrued_interest.percentage}
                />
              </Flex>
              {/* @ts-expect-error */}
              <Flex align="center" justify="space-between" py="8px">
                <Flex align="center">
                  {/* @ts-expect-error */}
                  <Text data-cy="bond-capital-gain-loss-2-label" mr="4px">
                    Capital Gain / Loss
                  </Text>
                  <Icon
                    component={InfoSvg}
                    style={{ color: '#969696' }}
                    onClick={() =>
                      openInfoModal(BondInfoType.CAPITAL_GAIN_LOSS)
                    }
                  />
                </Flex>
                <PnLCurrency
                  dataCy="bond-capital-gain-loss-2-content"
                  amount={details?.capital_gain_loss.amount}
                  percentage={details?.capital_gain_loss.percentage}
                />
              </Flex>
            </div>
          </Collapsible>
        </BorderedContainer>
      </SkeletonWrapper>

      <SkeletonWrapper
        skeleton={<PortfolioSkeletonBlock height={129} />}
        loading={isLoading}
      >
        <InfoTable
          dataSource={[
            {
              label: (
                <Flex align="center">
                  {/* @ts-expect-error */}
                  <Text data-cy="bond-total-units-label" mr="4px">
                    Total Unit Obligasi
                  </Text>
                  <Icon
                    component={InfoSvg}
                    style={{ color: '#969696' }}
                    onClick={() => openInfoModal(BondInfoType.TOTAL_UNIT)}
                  />
                </Flex>
              ),
              value: (
                <Text data-cy="bond-total-units-content">
                  {details?.total_units}
                </Text>
              ),
            },
            {
              label: (
                <Text data-cy="bond-average-price-label">
                  Harga Beli Rata-rata
                </Text>
              ),
              value: (
                <PnLCurrency
                  dataCy="bond-average-price-content"
                  amount={details?.price_rate}
                  isFillEmptyWithDash
                  isColorless
                  isSignless
                  isOnlyPercentage
                />
              ),
            },
            {
              label: (
                <Text data-cy="bond-yield-rate-label">Yield Rata-rata</Text>
              ),
              value: (
                <Text data-cy="bond-yield-rate-content">
                  {formatPercentage(
                    details?.yield_rate,
                    false,
                    '0,0.000',
                    true,
                  )}
                </Text>
              ),
            },
            {
              label: <Text data-cy="bond-due-date-label">Jatuh Tempo</Text>,
              value: (
                <Text data-cy="bond-due-date-content">
                  {formatBondDate(product?.meta?.maturity_date)}
                </Text>
              ),
            },

            {
              label: (
                <Flex align="center">
                  {/* @ts-expect-error */}
                  <Text data-cy="bond-projected-return-label" mr="4px">
                    Proyeksi Return
                  </Text>
                  <Icon
                    component={InfoSvg}
                    style={{ color: '#969696' }}
                    onClick={() => openInfoModal(BondInfoType.PROJECTED_RETURN)}
                  />
                </Flex>
              ),
              value: (
                <PnLCurrency
                  dataCy="bond-projected-return-content"
                  amount={details?.projected_return?.amount}
                  percentage={details?.projected_return?.percentage}
                  isFillEmptyWithDash
                />
              ),
            },
          ]}
        />
      </SkeletonWrapper>

      <SkeletonWrapper
        skeleton={<PortfolioSkeletonBlock height={129} />}
        loading={isLoading}
      >
        <InfoTable
          dataSource={[
            {
              label: <Text data-cy="bond-per-year-label">Kupon / Tahun</Text>,
              value: (
                <Text data-cy="bond-per-year-content">
                  {formatPercentage(
                    product?.meta?.coupon_rate,
                    false,
                    '0,0.000',
                    false,
                  )}
                </Text>
              ),
            },
            {
              label: (
                <Text data-cy="bond-next-date-label">
                  Tanggal Kupon Berikutnya
                </Text>
              ),
              value: (
                <Text data-cy="bond-next-date-content">
                  {formatBondDate(product?.meta?.next_coupon_date)}
                </Text>
              ),
            },
          ]}
        />
      </SkeletonWrapper>

      <SkeletonWrapper
        skeleton={<PortfolioSkeletonBlock height={111} />}
        loading={isLoading}
      >
        <SectionButton
          data-cy="transaction-history-button"
          className="button-unstyled full-button"
          onClick={handleCouponReceiptClick}
          type="button"
        >
          <InfoTable
            dataSource={
              histories?.length > 0
                ? [
                    {
                      label: (
                        <Text
                          data-cy="bond-receipt-history-label"
                          family="bold"
                          // @ts-expect-error
                          mr="4px"
                        >
                          Riwayat Penerimaan Kupon
                        </Text>
                      ),
                      value: <Icon component={ChevronRightSvg} />,
                    },
                    ...histories.map((history) => ({
                      label: (
                        <Text data-cy="bond-history-date-label">
                          {formatBondDate(history.date)}
                        </Text>
                      ),
                      value: (
                        <Text data-cy="bond-history-amount-content">
                          {formatBondCurrency(history.amount)}
                        </Text>
                      ),
                    })),
                  ]
                : []
            }
          />
        </SectionButton>
      </SkeletonWrapper>

      <ToHistoryButton symbol={symbol} />
    </>
  );
}

export default BondModalDetail;
